import { useState } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  getListOrganizationApiKeysQueryKey,
  getListOrganizationApiKeysQueryOptions,
  useRevokeOrganizationApiKey,
  type OrganizationApiKeyDto,
} from '@sara/api-client';
import { toSuspense } from '../../../../lib/query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@sara/ui/components/alert-dialog';
import { buttonVariants } from '@sara/ui/components/button';
import { toast } from '@sara/ui/components/sonner';
import { cn } from '@sara/ui/lib/utils';
import { ApiKeyList } from '../../components/ApiKeyList';
import type { ApiKeyListContainerProps } from './types';

export function ApiKeyListContainerContent({ orgId }: ApiKeyListContainerProps) {
  const queryClient = useQueryClient();
  const [keyToRevoke, setKeyToRevoke] = useState<OrganizationApiKeyDto | null>(null);

  const { data: apiKeys } = useSuspenseQuery(
    toSuspense(getListOrganizationApiKeysQueryOptions({ orgId }))
  );

  const { mutate: revoke, isPending: isRevoking } = useRevokeOrganizationApiKey({
    mutation: {
      onSuccess: (_data, variables) => {
        const revokedLabel = apiKeys.find((k) => k.id === variables.id)?.label ?? 'API key';
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationApiKeysQueryKey({ orgId }),
        });
        setKeyToRevoke(null);
        toast.success(`Revoked ${revokedLabel}`);
      },
      onError: (error) => {
        toast.error('Failed to revoke API key', {
          description: error instanceof Error ? error.message : undefined,
        });
      },
    },
  });

  return (
    <>
      <ApiKeyList
        apiKeys={apiKeys}
        onRevoke={setKeyToRevoke}
        revokingId={isRevoking && keyToRevoke ? keyToRevoke.id : null}
      />

      <AlertDialog
        open={keyToRevoke !== null}
        onOpenChange={(open) => {
          if (!open && !isRevoking) setKeyToRevoke(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API key?</AlertDialogTitle>
            <AlertDialogDescription>
              {keyToRevoke ? (
                <>
                  This immediately invalidates{' '}
                  <span className="font-medium text-foreground">{keyToRevoke.label}</span>. Any
                  integration using it will start receiving 401 errors. This cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(buttonVariants({ variant: 'outline', shape: 'pill', size: 'sm' }))}
              disabled={isRevoking}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive', shape: 'pill', size: 'sm' }))}
              disabled={isRevoking}
              onClick={(event) => {
                event.preventDefault();
                if (!keyToRevoke) return;
                revoke({ id: keyToRevoke.id, params: { orgId } });
              }}
            >
              {isRevoking ? 'Revoking…' : 'Revoke key'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
